import { createContext, useRef } from "react";
import { useSettings } from "../../../hooks/use-settings";
import { logger } from "../../../utils/logger";

const audioFiles = {
  recordingStart: "/audio/recording-start.mp3",
  recordingStop: "/audio/recording-stop.mp3",
  recordingCancel: "/audio/recording-cancel.mp3",
  goalSuccess: "/audio/goal-success.mp3",
  startConversation: "/audio/start-conversation.mp3",
};

export type UiAudio = ReturnType<typeof useUiAudio>;

export const UiAudioContext = createContext<UiAudio>({} as UiAudio);

export function useUiAudio() {
  const { settingsRef } = useSettings();
  const audioElementsRef = useRef<Record<keyof typeof audioFiles, HTMLAudioElement>>();

  const setupUiAudioElement = () => {
    if (!audioElementsRef.current) {
      // Preload all audio files to eliminate iOS playback delay.
      // Creating separate Audio elements for each sound and preloading them
      // ensures instant playback without the ~1 second delay caused by on-demand loading.
      audioElementsRef.current = Object.keys(audioFiles).reduce(
        (acc, key) => {
          const audioKey = key as keyof typeof audioFiles;
          const audio = new Audio();
          audio.src = audioFiles[audioKey];
          audio.preload = "auto";
          // Note that Safari does not support volume control for audio elements.
          // Hence, always 1.
          audio.volume = 1;
          // Preload the audio file
          audio.load();
          acc[audioKey] = audio;
          return acc;
        },
        {} as Record<keyof typeof audioFiles, HTMLAudioElement>,
      );
    }
  };

  const playUiAudio = async (
    audioKey: keyof typeof audioFiles,
    bypassSettings: boolean = false,
  ) => {
    if (!settingsRef.current.playbackUiAudio && !bypassSettings) {
      return;
    }

    try {
      setupUiAudioElement();
      const audio = audioElementsRef.current![audioKey];
      // Reset to start if already playing
      audio.currentTime = 0;
      await audio.play();
    } catch (e: any) {
      logger.error("Unable to play ui sound", e);
    }
  };

  const stopUiAudio = () => {
    if (audioElementsRef.current) {
      try {
        // Stop all audio elements
        Object.values(audioElementsRef.current).forEach((audio) => {
          audio.pause();
          audio.currentTime = 0;
        });
      } catch (e: any) {
        logger.error("Unable to stop ui sound", e);
      }
    }
  };

  return { playUiAudio, stopUiAudio, setupUiAudioElement };
}
